// Declarative screen registry — the single place that maps a navigable
// AdminScreen id to its sidebar group and rendered component. Adding a screen
// means adding one entry here (plus its role mapping in `api.ts`); App.tsx
// and AppShell.tsx stay untouched.
//
// Role-based visibility is NOT decided here: `navigableScreensFor` in
// `api.ts` remains the single source of truth (and is what the node-side
// test suites import — `api.ts` must stay free of component imports).
// Registry order must match `navigableScreensFor(['platform_admin'])`; the
// admin-web test suite asserts this invariant.

import type { ReactNode } from 'react';
import type { AdminScreen } from '../api.js';
import type { AdminHttpClient } from '../http.js';
import type { ToastMessage } from '../types.js';
import { AllocationsScreen } from './Allocations.js';
import { AnnouncementsScreen } from './Announcements.js';
import { AuditScreen } from './Audit.js';
import { GroupsScreen } from './Groups.js';
import { ModelsScreen } from './Models.js';
import { TenantLoginDefaultModelScreen } from './TenantLoginDefaultModel.js';
import { ExtensionsScreen } from './Extensions.js';
import { OverviewScreen } from './Overview.js';
import { PartnerScreen } from './Partner.js';
import { PartnersScreen } from './Partners.js';
import { PlatformConsole } from './PlatformConsole.js';
import { UsageScreen } from './Usage.js';
import { UsageLimitsScreen } from './limits/UsageLimitsScreen.js';
import { UsersScreen } from './Users.js';

export type NavGroupId = 'platform' | 'partner' | 'tenant';

/** A section IS a console: the sidebar shows one entry per section and that
 *  section's screens become its tab strip. Typed (rather than a free string) so
 *  a typo cannot invent a fifth console whose label falls back to the raw key,
 *  and so the console set is readable in one place. Each id needs a `nav.<id>`
 *  entry in BOTH locales. */
export type NavSectionId = 'monitoring' | 'members' | 'resources' | 'budget';

/** Sidebar groups, in render order. */
export const NAV_GROUPS: Array<{ id: NavGroupId; label: string }> = [
  { id: 'platform', label: 'Platform' },
  { id: 'partner', label: 'Partner' },
  { id: 'tenant', label: 'Tenant' },
];

/** Everything a screen may need from the app shell. `tenantHttp` is the
 *  act-as renewal-wrapped client for platform admins and the plain client
 *  for tenant-role sessions (App.tsx's `tenantScopedHttp`). */
export interface ScreenContext {
  platformHttp: AdminHttpClient;
  tenantHttp: AdminHttpClient;
  /** Tenant the tenant-scoped screens operate on (act-as tenant for platform
   *  admins, home tenant otherwise). */
  activeTenantId?: string;
  /** Currently selected act-as tenant (platform admins only). */
  selectedTenantId?: string;
  /** True when the logged-in operator is a platform admin (drives the
   *  Platform-Admin grant controls, which require the platform-scoped token). */
  isPlatformAdmin: boolean;
  /** True when the logged-in operator is a partner admin (edits its own
   *  tenants within the platform-granted scope, via the partner.* surface). */
  isPartnerAdmin: boolean;
  onToast: (toast: ToastMessage) => void;
  onTenantContext: (tenantId: string) => Promise<boolean>;
}

export interface ScreenDef {
  id: AdminScreen;
  label: string;
  group: NavGroupId;
  section?: NavSectionId;
  /** Platform admins must have an act-as tenant selected before this screen
   *  renders (true for every tenant-scoped screen). */
  requiresTenantContext: boolean;
  render: (ctx: ScreenContext) => ReactNode;
}

export const SCREEN_REGISTRY: ScreenDef[] = [
  {
    id: 'Platform Console',
    label: 'Platform Console',
    group: 'platform',
    requiresTenantContext: false,
    render: (ctx) => (
      <PlatformConsole
        http={ctx.platformHttp}
        tenantHttp={ctx.tenantHttp}
        onToast={ctx.onToast}
        selectedTenantId={ctx.selectedTenantId}
        onTenantContext={ctx.onTenantContext}
      />
    ),
  },
  {
    id: 'Partner',
    label: 'Partner',
    group: 'partner',
    requiresTenantContext: false,
    // Platform admins manage partners here (partner CRUD); partner admins get
    // their own operational console. Branching by role keeps the
    // partner-creation surface out of a partner admin's reach.
    render: (ctx) =>
      ctx.isPlatformAdmin ? (
        <PartnersScreen
          http={ctx.platformHttp}
          onToast={ctx.onToast}
          selectedTenantId={ctx.selectedTenantId}
        />
      ) : (
        <PartnerScreen
          http={ctx.platformHttp}
          onToast={ctx.onToast}
          onSetTenantContext={ctx.onTenantContext}
        />
      ),
  },
  {
    id: 'Overview',
    label: 'Overview',
    group: 'tenant',
    section: 'monitoring',
    requiresTenantContext: true,
    render: (ctx) => <OverviewScreen http={ctx.tenantHttp} />,
  },
  {
    id: 'Usage',
    label: 'Usage',
    group: 'tenant',
    section: 'monitoring',
    requiresTenantContext: true,
    render: (ctx) => <UsageScreen http={ctx.tenantHttp} />,
  },
  {
    // Read-only, like the two above — and an auditor or billing admin can reach
    // exactly these three plus the (read-only for them) announcements tab, so
    // they belong in one console.
    id: 'Audit',
    label: 'Audit',
    group: 'tenant',
    section: 'monitoring',
    requiresTenantContext: true,
    render: (ctx) => <AuditScreen http={ctx.tenantHttp} />,
  },
  {
    // The one WRITABLE screen in this console: owner/admin author tenant
    // notices here, while auditor/billing_admin see the list read-only. It must
    // stay adjacent to the other `monitoring` screens — the shell groups
    // CONSECUTIVE same-section entries, so a gap would render two consoles with
    // the same id.
    id: 'Announcements',
    label: 'Announcements',
    group: 'tenant',
    section: 'monitoring',
    requiresTenantContext: true,
    render: (ctx) => (
      <AnnouncementsScreen
        http={ctx.tenantHttp}
        activeTenantId={ctx.activeTenantId}
        onToast={ctx.onToast}
      />
    ),
  },
  {
    id: 'Users',
    label: 'Users',
    group: 'tenant',
    section: 'members',
    requiresTenantContext: true,
    render: (ctx) => (
      <UsersScreen
        http={ctx.tenantHttp}
        isPlatformAdmin={ctx.isPlatformAdmin}
        isPartnerAdmin={ctx.isPartnerAdmin}
        onToast={ctx.onToast}
      />
    ),
  },
  {
    id: 'Groups',
    label: 'Groups',
    group: 'tenant',
    section: 'members',
    requiresTenantContext: true,
    render: (ctx) => <GroupsScreen http={ctx.tenantHttp} onToast={ctx.onToast} />,
  },
  {
    id: 'Models',
    label: 'Models',
    group: 'tenant',
    section: 'resources',
    requiresTenantContext: true,
    render: (ctx) =>
      ctx.activeTenantId ? (
        <ModelsScreen
          http={ctx.tenantHttp}
          platformHttp={ctx.platformHttp}
          isPlatformAdmin={ctx.isPlatformAdmin}
          tenantId={ctx.activeTenantId}
          onToast={ctx.onToast}
        />
      ) : null,
  },
  {
    id: 'Default Model',
    label: 'Default Model',
    group: 'tenant',
    section: 'resources',
    requiresTenantContext: true,
    render: (ctx) =>
      ctx.activeTenantId ? (
        <TenantLoginDefaultModelScreen
          http={ctx.tenantHttp}
          tenantId={ctx.activeTenantId}
          onToast={ctx.onToast}
        />
      ) : null,
  },
  {
    id: 'Extension Apps',
    label: 'Extension Apps',
    group: 'tenant',
    section: 'resources',
    requiresTenantContext: true,
    render: (ctx) => (
      <ExtensionsScreen
        platformHttp={ctx.platformHttp}
        tenantHttp={ctx.tenantHttp}
        activeTenantId={ctx.activeTenantId}
        isPlatformAdmin={ctx.isPlatformAdmin}
        isPartnerAdmin={ctx.isPartnerAdmin}
        onToast={ctx.onToast}
      />
    ),
  },
  {
    // Money, not capability: put the budget in first, then cap spend against
    // it. `resources` (what a tenant may use) keeps Models + Extension Apps.
    id: 'Allocations',
    label: 'Allocations',
    group: 'tenant',
    section: 'budget',
    requiresTenantContext: true,
    render: (ctx) => <AllocationsScreen http={ctx.tenantHttp} onToast={ctx.onToast} />,
  },
  {
    id: 'Usage Limits',
    label: 'Usage Limits',
    group: 'tenant',
    section: 'budget',
    requiresTenantContext: true,
    render: (ctx) => <UsageLimitsScreen http={ctx.tenantHttp} onToast={ctx.onToast} />,
  },
];

/** The consoles of a sidebar group, in registry order. Derived rather than
 *  declared so a new screen cannot introduce a console the sidebar forgets —
 *  and because two lists of the same order drift. Assumes screens of one
 *  section stay adjacent (see ADMIN_SCREENS in api.ts). */
export function consoleSectionsOf(items: ScreenDef[]): NavSectionId[] {
  return [
    ...new Set(
      items.map((def) => def.section).filter((section): section is NavSectionId => Boolean(section))
    ),
  ];
}

export function screenDef(id: AdminScreen): ScreenDef | undefined {
  return SCREEN_REGISTRY.find((def) => def.id === id);
}
